import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';
import { validateIngredient } from '../utils/validation';

/**
 * CustomIngredientsService - Handles API operations for custom offer ingredients
 */

/**
 * Interface for custom ingredient
 */
export interface CustomIngredient {
  id: string;
  user_id: string;
  name: string;
  created_at: string;
}

// API endpoints
const API_BASE_URL = getApiBaseUrl();
const CUSTOM_INGREDIENTS_API = `${API_BASE_URL}/api/custom-ingredients`;

/**
 * Retrieves all custom ingredients for the current user
 * @param userId - The ID of the user whose ingredients to fetch
 * @returns {Promise<string[]>} Array of custom ingredient names
 */
export const getCustomIngredients = async (userId?: string): Promise<string[]> => {
  try {
    const response = await axios.get(`${CUSTOM_INGREDIENTS_API}/${userId || 'default'}`);
    return response.data.ingredients || [];
  } catch (error) {
    console.error('Error fetching custom ingredients:', error);
    throw new Error('Failed to fetch custom ingredients');
  }
};

/**
 * Saves a new custom ingredient for the current user
 * @param {string} name - The name of the custom ingredient
 * @param {string} userId - The ID of the user to save the ingredient for
 * @returns {Promise<{success: boolean, message: string}>} Result of the operation
 */
export const saveCustomIngredient = async (ingredient: string, userId?: string): Promise<{ success: boolean; message: string }> => {
  // Validate the ingredient
  const validation = validateIngredient(ingredient);
  if (!validation.isValid) {
    return { success: false, message: validation.message || 'Invalid ingredient' };
  }

  try {
    const response = await axios.post(CUSTOM_INGREDIENTS_API, {
      ingredient,
      userId: userId || 'default'
    });
    
    return { success: true, message: 'Custom ingredient saved successfully' };
  } catch (error) {
    console.error('Error saving custom ingredient:', error);
    return { success: false, message: 'Failed to save custom ingredient' };
  }
};

/**
 * Deletes a custom ingredient for the current user
 * @param {string} name - The name of the custom ingredient to delete
 * @param {string} userId - The ID of the user who owns the ingredient
 * @returns {Promise<{success: boolean, message: string}>} Result of the operation
 */
export const deleteCustomIngredient = async (ingredient: string, userId?: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.delete(CUSTOM_INGREDIENTS_API, {
      data: { 
        ingredient,
        userId: userId || 'default' 
      }
    });
    
    return { success: true, message: 'Custom ingredient deleted successfully' };
  } catch (error) {
    console.error('Error deleting custom ingredient:', error);
    return { success: false, message: 'Failed to delete custom ingredient' };
  }
};

export default {
  getCustomIngredients,
  saveCustomIngredient,
  deleteCustomIngredient
}; 